import React, { useState, useEffect, useRef, useCallback } from "react";
import { v4 as uuid } from "uuid";

import "../../App.css";
import { Button } from "../../components/ui/button";
import Input from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Toaster } from "../../components/ui/toaster";
import {
  Car,
  Phone,
  Mail,
  Search,
  Filter,
  Eye,
  Star,
  Fuel,
  Settings,
  Calendar,
  // Shield,
} from "lucide-react";

// Types
import {
  Car as CarType,
  CarFilters,
  InquiryFormData,
  TransmissionType,
  FuelType,
  CarCondition,
  CarCreate,
} from "../../types";

import Footer from "../templates/Footer";
import Header from "../templates/Header";
import ScrollUpPage from "../organisms/ScrollUpPage";
import { useVehicles } from "../../queries/vehicles.queries";
import { formatMileage, formatPrice, scrollToTop } from "../../lib/utils";
import CarDetailsModal from "../templates/CarDetailsModal";
import { useCreateInquiry } from "../../queries/inquiry.queries";
import { useToast } from "../../hooks/use-toast";

const Home: React.FC = () => {
  const { toast } = useToast();
  const [selectedCar, setSelectedCar] = useState<CarType | CarCreate | null>(
    null,
  );
  const [filters, setFilters] = useState<CarFilters>({
    make: "",
    minPrice: undefined,
    maxPrice: undefined,
    minYear: undefined,
    maxYear: undefined,
    fuelType: "all",
    transmission: "all",
    condition: "all",
  });
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [showInquiryForm, setShowInquiryForm] = useState<boolean>(false);
  const [inquiryForm, setInquiryForm] = useState<InquiryFormData>({
    customerName: "",
    customerEmail: "",
    customerPhone: "",
    message: "I'm interested in this vehicle",
  });
  const [showPageUp, setShowPageUp] = useState(false);

  function useDebounce<T>(value: T, delay = 500) {
    const [debounced, setDebounced] = useState(value);

    useEffect(() => {
      const timer = setTimeout(() => setDebounced(value), delay);
      return () => clearTimeout(timer);
    }, [value, delay]);

    return debounced;
  }

  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const debouncedMake = useDebounce(filters.make);
  const debouncedMinPrice = useDebounce(filters.minPrice);
  const debouncedMaxPrice = useDebounce(filters.maxPrice);
  const debouncedMinYear = useDebounce(filters.minYear);
  const debouncedMaxYear = useDebounce(filters.maxYear);

  const { data, fetchNextPage, isFetchingNextPage, hasNextPage } = useVehicles({
    limit: 5,
    make: debouncedMake,
    search: debouncedSearchTerm,
    minPrice: debouncedMinPrice,
    maxPrice: debouncedMaxPrice,
    minYear: debouncedMinYear,
    maxYear: debouncedMaxYear,
    fuelType: filters.fuelType === "all" ? undefined : filters.fuelType,
    transmission:
      filters.transmission === "all" ? undefined : filters.transmission,
    condition: filters.condition === "all" ? undefined : filters.condition,
  });
  const totalNumOfVehicles = data?.pages?.[0]?.total;
  const vehiclesResult = data?.pages.flatMap((p) => p.vehicles);

  const observer = useRef<IntersectionObserver | null>(null);

  const lastCarElementRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (isFetchingNextPage || !hasNextPage) return;

      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasNextPage) {
          fetchNextPage();
        }
      });

      if (node) observer.current.observe(node);
    },
    [isFetchingNextPage, hasNextPage, fetchNextPage],
  );

  useEffect(() => {
    const onScroll = () => {
      setShowPageUp(window.scrollY > 300);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleFilterChange = (
    key: keyof CarFilters,
    value: string | undefined,
  ): void => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const clearFilters = (): void => {
    setFilters({
      make: "",
      minPrice: undefined,
      maxPrice: undefined,
      minYear: undefined,
      maxYear: undefined,
      fuelType: "all",
      transmission: "all",
      condition: "all",
    });
    setSearchTerm("");
  };

  const handleCarSelect = (car: CarType): void => {
    setSelectedCar(car);
  };

  const handleInquirySelect = (car: CarType): void => {
    setSelectedCar(car);
    setShowInquiryForm(true);
  };

  const createInquiry = useCreateInquiry();

  const handleSubmitInquiry = async (selectedVehicle: CarType | CarCreate) => {
    if (
      !selectedVehicle ||
      inquiryForm.customerEmail.trim() === "" ||
      inquiryForm.customerName.trim() === "" ||
      inquiryForm.customerPhone.trim() === "" ||
      inquiryForm.message.trim() === ""
    ) {
      toast({
        title: "Error",
        description: "Please fill in all the details",
        variant: "destructive",
      });
      return;
    }
    const subject = `${selectedVehicle.year} ${selectedVehicle.make} ${selectedVehicle.model} - ${formatPrice(selectedVehicle.price)}`;
    const inquiryPayload = {
      id: uuid(),
      email: inquiryForm.customerEmail,
      full_name: inquiryForm.customerName,
      phone_number: inquiryForm.customerPhone,
      subject,
      message: inquiryForm.message,
    };

    try {
      await createInquiry.mutateAsync(inquiryPayload);
      setShowInquiryForm(false);
      toast({
        title: "Success!",
        description: "Inquiry submitted! We will reach out to you soon!",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create inquiry",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <Header />
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-900 via-orange-400 to-slate-800 text-white py-20">
        <div className="absolute inset-0 bg-black opacity-20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-5xl font-bold mb-6">Find Your Perfect Vehicle</h2>
          <p className="text-xl mb-8 text-blue-100">
            Discover premium vehicles from trusted dealers with competitive
            prices
          </p>

          {/* Search Bar */}
          <div className="max-w-2xl mx-auto">
            <div className="relative">
              <Search className="absolute left-4 top-4 h-5 w-5 text-gray-400" />
              <Input
                type="text"
                placeholder="Search by make, model, or color..."
                value={searchTerm}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^[A-Za-z\s]*$/.test(value)) {
                    setSearchTerm(value);
                  }
                }}
                className="pl-12 pr-4 py-4 text-lg border-0 bg-white/90 backdrop-blur-sm"
              />
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Filters Sidebar */}
          <div className="lg:col-span-1">
            <Card className="sticky top-8">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center">
                    <Filter className="mr-2 h-5 w-5" />
                    Filters
                  </span>
                  <Button variant="outline" size="sm" onClick={clearFilters}>
                    Clear All
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <Label>Make</Label>
                  <Input
                    placeholder="e.g. Toyota, Honda..."
                    value={filters.make}
                    onChange={(e) => handleFilterChange("make", e.target.value)}
                  />
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label>Min Price (RM)</Label>
                    <Input
                      type="number"
                      placeholder="RM 0"
                      value={filters.minPrice}
                      onChange={(e) =>
                        handleFilterChange("minPrice", e.target.value)
                      }
                    />
                  </div>
                  <div>
                    <Label>Max Price (RM)</Label>
                    <Input
                      type="number"
                      placeholder="RM 100000"
                      value={filters.maxPrice}
                      onChange={(e) =>
                        handleFilterChange("maxPrice", e.target.value)
                      }
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label>Min Year</Label>
                    <Input
                      type="number"
                      placeholder="2015"
                      value={filters.minYear}
                      onChange={(e) =>
                        handleFilterChange("minYear", e.target.value)
                      }
                    />
                  </div>
                  <div>
                    <Label>Max Year</Label>
                    <Input
                      type="number"
                      placeholder="2025"
                      value={filters.maxYear}
                      onChange={(e) =>
                        handleFilterChange("maxYear", e.target.value)
                      }
                    />
                  </div>
                </div>

                <div>
                  <Label>Fuel Type</Label>
                  <Select
                    value={filters.fuelType}
                    onValueChange={(value) =>
                      handleFilterChange(
                        "fuelType",
                        value === "all" ? "all" : (value as FuelType),
                      )
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Any" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Any</SelectItem>
                      <SelectItem value={FuelType.PETROL}>Petrol</SelectItem>
                      <SelectItem value={FuelType.DIESEL}>Diesel</SelectItem>
                      <SelectItem value={FuelType.ELECTRIC}>
                        Electric
                      </SelectItem>
                      <SelectItem value={FuelType.HYBRID}>Hybrid</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Transmission</Label>
                  <Select
                    value={filters.transmission}
                    onValueChange={(value) =>
                      handleFilterChange(
                        "transmission",
                        value === "all" ? "all" : (value as TransmissionType),
                      )
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Any" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Any</SelectItem>
                      <SelectItem value={TransmissionType.AUTOMATIC}>
                        Automatic
                      </SelectItem>
                      <SelectItem value={TransmissionType.MANUAL}>
                        Manual
                      </SelectItem>
                      <SelectItem value={TransmissionType.CVT}>CVT</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Condition</Label>
                  <Select
                    value={filters.condition}
                    onValueChange={(value) =>
                      handleFilterChange(
                        "condition",
                        value === "all" ? "all" : (value as CarCondition),
                      )
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Any" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Any</SelectItem>
                      <SelectItem value={CarCondition.NEW}>New</SelectItem>
                      <SelectItem value={CarCondition.USED}>Used</SelectItem>
                      <SelectItem value={CarCondition.CERTIFIED_PRE_OWNED}>
                        Certified Pre-Owned
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Vehicles Grid */}
          <div className="lg:col-span-3">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-semibold text-gray-900">
                {totalNumOfVehicles} Vehicles Available
              </h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {vehiclesResult &&
                vehiclesResult.map((car, index) => (
                  <Card
                    key={car.id}
                    ref={
                      index === vehiclesResult.length - 1
                        ? lastCarElementRef
                        : undefined
                    }
                    className="group hover:shadow-xl transition-all duration-300 border-0 shadow-md"
                  >
                    <div className="relative overflow-hidden rounded-t-lg">
                      <img
                        src={car.images[0]}
                        alt={`${car.make} ${car.model}`}
                        className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute top-4 left-4">
                        <Badge
                          variant={
                            car.condition === CarCondition.NEW
                              ? "default"
                              : "secondary"
                          }
                          className="bg-white/90 text-gray-900 backdrop-blur-sm"
                        >
                          {car.condition}
                        </Badge>
                      </div>
                    </div>

                    <CardContent className="p-6">
                      <div className="mb-4">
                        <h4 className="text-xl font-bold text-gray-900 mb-1">
                          {car.year} {car.make} {car.model}
                        </h4>
                        <p className="text-3xl font-bold text-blue-600 mb-2">
                          {formatPrice(car.price)}
                        </p>
                      </div>

                      <div className="grid grid-cols-2 gap-4 text-sm text-gray-600 mb-4">
                        <div className="flex items-center">
                          <Calendar className="mr-2 h-4 w-4" />
                          {formatMileage(car.mileage)} km
                        </div>
                        <div className="flex items-center">
                          <Fuel className="mr-2 h-4 w-4" />
                          {car.fuel_type}
                        </div>
                        <div className="flex items-center">
                          <Settings className="mr-2 h-4 w-4" />
                          {car.transmission}
                        </div>
                        <div className="flex items-center">
                          <Star className="mr-2 h-4 w-4" />
                          {car.horsepower} HP
                        </div>
                      </div>

                      <div className="flex space-x-2">
                        <Button
                          onClick={() => handleCarSelect(car)}
                          className="flex-1"
                          variant="outline"
                        >
                          <Eye className="mr-2 h-4 w-4" />
                          Details
                        </Button>
                        <Button
                          onClick={() => handleInquirySelect(car)}
                          className="flex-1"
                        >
                          <Phone className="mr-2 h-4 w-4" />
                          Inquire
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
            </div>

            {vehiclesResult && vehiclesResult.length === 0 && (
              <div className="text-center py-12">
                <Car className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <h3 className="text-xl font-medium text-gray-900 mb-2">
                  No vehicles found
                </h3>
                <p className="text-gray-500">
                  Try adjusting your search criteria
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Car Details Modal */}
      {selectedCar && !showInquiryForm && (
        <CarDetailsModal
          selectedCar={selectedCar}
          setSelectedCar={setSelectedCar}
          setShowInquiryForm={(val) => setShowInquiryForm(val)}
        />
      )}

      {/* Inquiry Form Modal */}
      {showInquiryForm && selectedCar && (
        <Dialog
          open={showInquiryForm}
          onOpenChange={() => setShowInquiryForm(false)}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Inquire About This Vehicle</DialogTitle>
              <DialogDescription>
                {selectedCar.year} {selectedCar.make} {selectedCar.model} -{" "}
                {formatPrice(selectedCar.price)}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div>
                <Label htmlFor="name">Full Name *</Label>
                <Input
                  id="name"
                  value={inquiryForm.customerName}
                  onChange={(e) =>
                    setInquiryForm((prev) => ({
                      ...prev,
                      customerName: e.target.value,
                    }))
                  }
                  placeholder="Your full name"
                />
              </div>

              <div>
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  type="email"
                  value={inquiryForm.customerEmail}
                  onChange={(e) =>
                    setInquiryForm((prev) => ({
                      ...prev,
                      customerEmail: e.target.value,
                    }))
                  }
                  placeholder="your.email@example.com"
                />
              </div>

              <div>
                <Label htmlFor="phone">Phone Number *</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={inquiryForm.customerPhone}
                  onChange={(e) =>
                    setInquiryForm((prev) => ({
                      ...prev,
                      customerPhone: e.target.value,
                    }))
                  }
                  placeholder="+(60) 123-4567"
                />
              </div>

              <div>
                <Label htmlFor="message">Message</Label>
                <Textarea
                  id="message"
                  value={inquiryForm.message}
                  onChange={(e) =>
                    setInquiryForm((prev) => ({
                      ...prev,
                      message: e.target.value,
                    }))
                  }
                  placeholder="I'm interested in this vehicle..."
                  rows={4}
                />
              </div>

              <div className="flex space-x-2">
                <Button
                  onClick={() => handleSubmitInquiry(selectedCar)}
                  className="flex-1"
                >
                  <Mail className="mr-2 h-4 w-4" />
                  Send Inquiry
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowInquiryForm(false)}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {showPageUp && <ScrollUpPage handlePageUp={scrollToTop} />}

      {/* Footer */}
      <Footer />

      <Toaster />
    </div>
  );
};

export default Home;
